import { setItemVisual } from './icons';
import { Inventory, ITEMS } from '../game/inventory';
import { RECIPES, CRAFT_CATEGORIES, categoryOf, craftSeconds, type Recipe, type CraftCategory } from './crafting';
import { CraftQueue } from '../game/craftqueue';
import { isUnlocked } from '../game/unlocks';
import { researchGated, RESEARCH_GATE_ALL } from '../game/research';
import { SK, labelCss, btnCss, kbd, injectSkinCss } from './skin';
import { buildShell, threeCol, paneHead, slotEl, SHELL_DIMS, type Shell } from './shell';

// Bulk-craft batch sizes for the detail pane's x1/x10/Max control. Max is clamped so a huge stockpile
// cannot enqueue an unbounded run; dev mode (free + instant) crafts a fixed batch for Max.
const MAX_CRAFT_BATCH = 100;
const DEV_MAX_BATCH = 10;
const QUEUE_SLOT = 56; // slotEl('queue') height: each queue entry is a 56px slot (spec section 4)
const DETAIL_TILE = 64; // spec: the detail pane's icon tile is a standard 64 slot
const QUEUE_BAR_H = 3;  // spec: 3px accent progress bar on the active job

// The starter items float to the top of every unfiltered list (owner ask: rock, torch, guidebook, then bandage
// and the tool tiers). Anything not listed sorts after these by name.
const CRAFT_PRIORITY = ['rock', 'torch', 'guidebook', 'bandage', 'wood_axe', 'wood_pickaxe', 'stone_axe', 'stone_pickaxe', 'metal_axe', 'metal_pickaxe'];
function craftPriority(id: string): number { const i = CRAFT_PRIORITY.indexOf(id); return i < 0 ? 999 : i; }

// Bounded Levenshtein for the typo-tolerant search (small strings, cheap).
function editDistance(a: string, b: string): number {
  const m = a.length, n = b.length;
  if (!m) return n; if (!n) return m;
  let prev = Array.from({ length: n + 1 }, (_, i) => i);
  for (let i = 1; i <= m; i++) {
    const cur = [i];
    for (let j = 1; j <= n; j++) cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    prev = cur;
  }
  return prev[n];
}

// Fuzzy match score of query `q` against item `name` (both already lowercase). 0 = no match; higher = better.
// Exact > prefix > substring > in-order subsequence (skips typos/missing letters) > small edit-distance (wrong
// letters). Lets "wodden ax" / "picaxe" / "torhc" still find the right recipe.
function fuzzyScore(q: string, name: string): number {
  if (!q) return 1;
  if (name === q) return 1000;
  if (name.startsWith(q)) return 820 - name.length;
  const idx = name.indexOf(q);
  if (idx >= 0) return 640 - idx - name.length * 0.1;
  let qi = 0; for (let i = 0; i < name.length && qi < q.length; i++) if (name[i] === q[qi]) qi++;
  if (qi === q.length) return 360 - name.length;
  if (q.length >= 3) {
    // compare against each word and the head of the name; tolerate ~1 typo per 4 chars
    const words = name.split(/\s+/); let best = 99;
    for (const w of [name.slice(0, q.length + 2), ...words]) best = Math.min(best, editDistance(q, w));
    const tol = Math.max(1, Math.floor(q.length / 4));
    if (best <= tol) return 240 - best * 30;
  }
  return 0;
}

// The CRAFTING menu (Q). Dedicated screen: a category rail on the left, a searchable recipe list in the
// middle, and the live craft QUEUE on the right (progress bar on the head job, ✕ to cancel + refund).
// Crafting spends the cost immediately and the item lands when its timer completes (hotbar first).
// DEV MODE (` overlay) crafts everything FREE + INSTANT. A mini queue strip (QueueHud below) stays on
// screen while jobs run so you can close the menu and keep playing.
//
// PROGRESSION gates (recipe.tier / recipe.locked, ui/crafting.ts): a tiered recipe greys out with
// "Needs Workbench N" unless benchTier() (nearest placed bench, deployables.nearestBenchTier) is high
// enough; a locked recipe (guns/explosives/reinforced) greys out with "Research required" until
// game/unlocks.ts says it's unlocked. Both gates are skipped in dev mode, same as the cost check.

type RailCategory = 'All' | 'Favorites' | CraftCategory;
const FAV_KEY = 'oxide.favRecipes';

// Hover states for the menu's selectable rows/buttons, per the style guide: category buttons light
// ACCENT-SOFT (orange = interactive), list rows raise NEUTRALLY (surface-2 + strong border) so the
// per-item affordability colours stay readable. Injected because :hover can't be expressed inline.
const OXCM_CSS = `
.oxcm-cat:hover{background:${SK.accentSoft}!important;color:${SK.textHi}!important;border-color:${SK.accent}!important;}
.oxcm-row{transition:background ${SK.tFast} ${SK.ease},border-color ${SK.tFast} ${SK.ease};}
.oxcm-row:hover{background:${SK.surface2}!important;border-color:${SK.borderStrong}!important;}
.oxcm-search:focus{border-color:${SK.accent}!important;box-shadow:0 0 0 3px ${SK.accentSoft};}
`;

export class CraftMenu {
  private readonly shell: Shell;
  private readonly catRail: HTMLDivElement;
  private readonly searchInput: HTMLInputElement;
  private readonly recipeList: HTMLDivElement;
  private readonly detailBox: HTMLDivElement; // column 3, top: the selected-recipe detail
  private readonly queuePane: HTMLDivElement; // column 3, below the detail: the live craft queue
  private selected: Recipe | null = null;     // the recipe shown in the detail pane (defaults to the first row)
  private craftQty: 1 | 10 | 'max' = 1;       // detail-pane batch selector (x1 / x10 / Max)
  private open_ = false;
  private category: RailCategory = 'All';
  private search = '';
  private raf = 0;
  private viewMode: 'list' | 'grid' = loadView(); // recipe display: detailed list or compact icon grid
  private viewToggle!: HTMLButtonElement;
  private readonly favorites = new Set<string>(loadFavorites());
  onPin: ((id: string) => void) | null = null;       // toggle a HUD pin (main wires PinnedHud)
  isPinned: ((id: string) => boolean) | null = null; // paint state for the 📌 button
  private readonly queueBars: Array<{ fill: HTMLDivElement; secs: HTMLSpanElement; index: number }> = [];

  onClose: (() => void) | null = null;
  onCraft: ((r: Recipe) => void) | null = null;          // main routes: dev = instant free, else spend + enqueue
  onCancel: ((index: number) => void) | null = null;     // main refunds + removes from the queue

  constructor(
    private readonly inv: Inventory,
    private readonly queue: CraftQueue,
    private readonly isDev: () => boolean,
    private readonly benchTier: () => number = () => 0, // nearest placed bench tier in range; default 0 for callers that don't wire it (tests)
  ) {
    injectSkinCss();
    if (!document.getElementById('oxcm-style')) {
      const s = document.createElement('style');
      s.id = 'oxcm-style';
      s.textContent = OXCM_CSS;
      document.head.appendChild(s);
    }

    // THE shared 1240 shell (oxide-shell-spec.md sections 0 + 3): 'Crafting', close key Q, body layout C
    // (three columns). The footer owns the interaction hints; the header owns the optional dev-mode badge.
    this.shell = buildShell({
      title: 'Crafting', closeKey: 'Q', bodyPad: false,
      hintsHtml: `Click a recipe for details &middot; &#9733; favourite &middot; &#128204; pin to HUD &middot; ${kbd('Q')} close`,
    });
    const outsideTap = (e: MouseEvent): boolean => e.target === this.shell.scrim;
    this.shell.scrim.addEventListener('mouseup', (e) => { if (e.button === 0 && outsideTap(e)) this.close(); });
    this.shell.scrim.addEventListener('contextmenu', (e) => { e.preventDefault(); if (outsideTap(e)) this.close(); });

    const { cats, list: listCol, detail } = threeCol(this.shell);

    // COLUMN 1 (190): the category rail. Fills the column width (the pane owns the 24 padding).
    this.catRail = cats;
    this.catRail.style.gap = '4px';

    // COLUMN 2 (1fr): a pane head carrying the LIST / GRID view toggle, then a full-width 36px search
    // input, then the scrolling recipe list.
    const listHead = paneHead('Recipes');
    this.viewToggle = document.createElement('button');
    this.viewToggle.className = 'oxcm-cat';
    this.viewToggle.style.cssText = btnCss('secondary', true);
    this.viewToggle.addEventListener('click', () => { this.viewMode = this.viewMode === 'list' ? 'grid' : 'list'; saveView(this.viewMode); this.syncViewToggle(); this.renderRecipes(); });
    listHead.appendChild(this.viewToggle);
    this.syncViewToggle();
    listCol.appendChild(listHead);

    this.searchInput = document.createElement('input');
    this.searchInput.placeholder = 'Search recipes ...';
    this.searchInput.className = 'oxcm-search';
    this.searchInput.style.cssText = css('box-sizing:border-box', 'width:100%', 'height:36px', 'padding:0 12px', 'flex:none', 'outline:none', 'margin-bottom:8px',
      `background:${SK.well}`, `color:${SK.textHi}`, `border:1px solid ${SK.border}`, `border-radius:${SK.rMd}`, 'font:inherit', `transition:all ${SK.tFast} ${SK.ease}`);
    this.searchInput.addEventListener('input', () => { this.search = this.searchInput.value.toLowerCase(); this.renderRecipes(); });
    for (const ev of ['keydown', 'keyup']) this.searchInput.addEventListener(ev, (e) => e.stopPropagation()); // keys must not reach the game
    this.recipeList = document.createElement('div');
    this.recipeList.style.cssText = css('display:flex', 'flex-direction:column', 'gap:8px', 'overflow-y:scroll', 'scrollbar-gutter:stable', 'flex:1', 'min-height:0');
    listCol.appendChild(this.searchInput);
    listCol.appendChild(this.recipeList);

    // COLUMN 3 (400): the selected-recipe detail on top, then the live craft QUEUE below it.
    this.detailBox = document.createElement('div');
    this.detailBox.style.cssText = css('display:flex', 'flex-direction:column', 'gap:12px', 'flex:none');
    const qt = document.createElement('div');
    qt.textContent = 'QUEUE';
    qt.style.cssText = labelCss() + `;flex:none;margin-top:${SHELL_DIMS.gap}px;margin-bottom:8px`;
    this.queuePane = document.createElement('div');
    this.queuePane.style.cssText = css('display:flex', 'flex-direction:column', 'gap:8px', 'overflow-y:auto', 'flex:1', 'min-height:0');
    detail.appendChild(this.detailBox);
    detail.appendChild(qt);
    detail.appendChild(this.queuePane);

    document.body.appendChild(this.shell.scrim);

    this.inv.subscribe(() => { if (this.open_) this.renderRecipes(); }); // affordability colours live-update
    this.queue.onChange = () => { if (this.open_) this.renderQueue(); };
  }

  get isOpen(): boolean { return this.open_; }

  toggle(): void { this.open_ ? this.close() : this.show(); }

  show(): void {
    if (this.open_) return;
    this.open_ = true;
    this.shell.scrim.style.display = 'flex';
    this.syncDevBadge();
    this.lastTier = this.benchTier();
    this.renderAll();
    this.raf = requestAnimationFrame(() => this.tick());
  }

  close(): void {
    if (!this.open_) return;
    this.open_ = false;
    this.shell.scrim.style.display = 'none';
    if (this.raf) { cancelAnimationFrame(this.raf); this.raf = 0; }
    this.onClose?.();
  }

  // DEV MODE (` overlay) crafts free + instant; surface it as a warn-coloured header badge (spec: the
  // badge sits left of the close hint), or clear it when dev mode is off.
  private syncDevBadge(): void {
    this.shell.setBadge(this.isDev()
      ? `<span class="ox-badge ox-badge--t3">Dev &middot; free + instant</span>`
      : null);
  }

  private lastTier = -1; // last benchTier() seen by tick() -- -1 forces a render on first open

  private tick(): void {
    if (!this.open_) return;
    this.raf = requestAnimationFrame(() => this.tick());
    // Only the head job's bar moves; update widths in place (no re-render churn).
    const now = performance.now();
    for (const b of this.queueBars) {
      b.fill.style.width = `${(this.queue.progress(b.index, now) * 100).toFixed(1)}%`;
      b.secs.textContent = `${Math.ceil(this.queue.remainingSec(b.index, now))}s`;
    }
    // Walking into/out of a bench's range while the menu is open should re-grey recipes live (owner
    // spec: greys out near a workbench of the right level). Cheap to poll -- one int compare per frame.
    const t = this.benchTier();
    if (t !== this.lastTier) { this.lastTier = t; this.renderRecipes(); }
  }

  private renderAll(): void {
    this.renderCats();
    this.renderRecipes();
    this.renderQueue();
  }

  private renderCats(): void {
    this.catRail.replaceChildren();
    const cats: RailCategory[] = ['All', ...CRAFT_CATEGORIES, 'Favorites'];
    for (const c of cats) {
      const active = c === this.category;
      const b = document.createElement('button');
      b.className = 'oxcm-cat';
      b.textContent = c;
      // Category rail per the crafting-window spec: active = accent-soft fill + an inset accent bar.
      b.style.cssText = css('padding:8px 10px', `border-radius:${SK.rMd}`, 'font:inherit', `font-size:${SK.fsSmall}`, 'font-weight:700', 'text-align:left', 'cursor:pointer', 'border:1px solid transparent', `transition:all ${SK.tFast} ${SK.ease}`,
        active ? `background:${SK.accentSoft};color:${SK.textHi};box-shadow:inset 2px 0 0 ${SK.accent}` : `background:transparent;color:${SK.dim}`);
      b.addEventListener('click', () => { this.category = c; this.renderCats(); this.renderRecipes(); });
      this.catRail.appendChild(b);
    }
  }

  private syncViewToggle(): void {
    // The button shows the CURRENT mode (with an icon); clicking flips it.
    this.viewToggle.textContent = this.viewMode === 'list' ? '☰ List' : '▦ Grid';
    this.viewToggle.title = 'Switch recipe view (list / grid)';
  }

  // Recipe metadata shared by both views: research gate, WORKBENCH-tier gate, affordability, and the station
  // label. A bench-gated recipe (its tier is above your nearest workbench) now SHOWS but darkened + tagged,
  // instead of being hidden -- so the player can see everything a bench would unlock. `gated` covers both.
  private recipeState(r: Recipe, dev: boolean): { needsResearch: boolean; benchGated: boolean; needStation: string; gated: boolean; afford: boolean } {
    // Research gate: with the full tree on, EVERY tree node needs unlocking; otherwise fall back to the
    // narrow `locked` set (guns/explosives/reinforced). Either way, dev mode skips it.
    const needsResearch = !dev && (researchGated(r.id, (x) => isUnlocked(x as typeof r.id)) || (!RESEARCH_GATE_ALL && !!r.locked && !isUnlocked(r.id)));
    const reqTier = r.tier ?? 0;
    const benchGated = !dev && reqTier > this.benchTier();
    const needStation = reqTier > 0 ? `Workbench Lvl ${reqTier}` : '';
    const gated = needsResearch || benchGated;
    const afford = dev || (r.cost.every((c) => this.inv.count(c.item) >= c.amount) && !gated);
    return { needsResearch, benchGated, needStation, gated, afford };
  }

  private tryCraft(r: Recipe, gated: boolean): void {
    if (!this.isDev() && (gated || !r.cost.every((c) => this.inv.count(c.item) >= c.amount))) return;
    this.onCraft?.(r);
  }

  private toggleFav(r: Recipe): void {
    if (this.favorites.has(r.id)) this.favorites.delete(r.id); else this.favorites.add(r.id);
    saveFavorites(this.favorites);
    this.renderRecipes();
  }

  private renderRecipes(): void {
    const dev = this.isDev();
    this.syncDevBadge();
    const list = this.recipeList;
    list.replaceChildren();
    // Container layout differs per mode: a vertical stack of rows, or an auto-filling icon grid.
    list.style.cssText = this.viewMode === 'grid'
      ? css('display:grid', 'grid-template-columns:repeat(auto-fill,minmax(94px,1fr))', 'gap:8px', 'align-content:start', 'overflow-y:scroll', 'scrollbar-gutter:stable', 'flex:1', 'min-height:0')
      : css('display:flex', 'flex-direction:column', 'gap:8px', 'overflow-y:scroll', 'scrollbar-gutter:stable', 'flex:1', 'min-height:0');
    // SEARCH: when there's a query, it's typo-tolerant AND spans EVERY category (not just the selected rail), so
    // you never miss a recipe because you were on the wrong tab. With no query, the rail category filters as before.
    const q = this.search.trim();
    const searching = q.length > 0;
    const shown = RECIPES.filter((r) => {
      if (searching) return fuzzyScore(q, ITEMS[r.id].name.toLowerCase()) > 0; // all categories
      if (this.category === 'Favorites') return this.favorites.has(r.id);
      if (this.category !== 'All') return categoryOf(r.id) === this.category;
      return true; // above-tier recipes now SHOW (darkened + tagged with the needed workbench), no longer hidden
    });
    // Order: while searching, best fuzzy match first (then starter-priority, then name). With no query, craftable-
    // now first, then bench-gated, then research-locked, with the starter items floated to the top of each block.
    if (searching) {
      shown.sort((a, b) => fuzzyScore(q, ITEMS[b.id].name.toLowerCase()) - fuzzyScore(q, ITEMS[a.id].name.toLowerCase())
        || craftPriority(a.id) - craftPriority(b.id) || ITEMS[a.id].name.localeCompare(ITEMS[b.id].name));
    } else {
      const rank = (r: Recipe): number => { const st = this.recipeState(r, dev); return st.needsResearch ? 2 : st.benchGated ? 1 : 0; };
      shown.sort((a, b) => rank(a) - rank(b) || craftPriority(a.id) - craftPriority(b.id) || ITEMS[a.id].name.localeCompare(ITEMS[b.id].name));
    }
    // Keep the detail pane's selection valid: default to (or fall back to) the first shown recipe when the
    // current pick has been filtered out of the list.
    if (!shown.some((r) => r === this.selected)) this.selected = shown[0] ?? null;
    if (!shown.length) {
      const empty = document.createElement('div');
      empty.textContent = this.category === 'Favorites' ? 'No favorites yet. Tap a star to add one.' : 'Nothing matches.';
      empty.style.cssText = css(`color:${SK.faint}`, `font-size:${SK.fsSmall}`, 'padding:8px 2px');
      list.appendChild(empty);
      this.renderDetail();
      return;
    }
    for (const r of shown) list.appendChild(this.viewMode === 'grid' ? this.gridCell(r, dev) : this.listRow(r, dev));
    this.renderDetail();
  }

  // Selecting a recipe (clicking its row/tile) shows it in the detail pane. Cheap: only the detail
  // repaints, and the row's `selected` outline is refreshed by a light list re-render.
  private selectRecipe(r: Recipe): void {
    if (this.selected === r) return;
    this.selected = r;
    this.renderRecipes();
  }

  // Detailed row: star, colour swatch, name + cost/time, a Craft button.
  private listRow(r: Recipe, dev: boolean): HTMLDivElement {
    const { needsResearch, benchGated, needStation, gated, afford } = this.recipeState(r, dev);
    const sel = r === this.selected;
    const row = document.createElement('div');
    row.className = 'oxcm-row';
    row.style.cssText = css('display:flex', 'align-items:center', 'gap:8px', 'padding:9px 12px', 'flex:none', 'cursor:pointer',
      `background:${sel ? SK.accentSoft : SK.raised}`, `border:1px solid ${sel ? SK.accent : SK.border}`, `border-radius:${SK.rMd}`, gated ? 'opacity:0.55' : 'opacity:1');
    row.addEventListener('click', () => this.selectRecipe(r)); // clicking the row body selects it for the detail pane
    row.appendChild(this.favStar(r));
    const sw = document.createElement('span');
    sw.style.cssText = css('width:38px', 'height:38px', `border-radius:${SK.rSm}`, `background:${cssHex(ITEMS[r.id].color)}`, 'flex:none');
    setItemVisual(sw, r.id, cssHex(ITEMS[r.id].color));
    const label = document.createElement('div');
    label.style.cssText = css('flex:1', 'min-width:0');
    // Costs read dim when affordable and BAD only when short; gates are warn (style guide law 3).
    const gateStr = needsResearch
      ? `<span style="color:${SK.warn}">Research required</span>`
      : benchGated
      ? `<span style="color:${SK.warn}">Needs ${needStation}</span>`
      : dev
      ? `<span style="color:${SK.good}">FREE</span>`
      : r.cost.map((c) => { const ok = this.inv.count(c.item) >= c.amount; return `<span style="color:${ok ? SK.dim : SK.bad}">${c.amount} ${ITEMS[c.item].name}</span>`; }).join(', ');
    label.innerHTML = `<span style="font-weight:700;color:${SK.textHi}">${ITEMS[r.id].name}</span><br><span style="font-size:${SK.fsMicro}">${gateStr} · ${dev ? 'instant' : `${craftSeconds(r)}s`}</span>`;
    // PIN (owner QoL): a top-right HUD checklist tracks the pinned recipe's missing mats while you farm.
    const pin = document.createElement('button');
    const pinned = this.isPinned?.(r.id) ?? false;
    pin.textContent = '📌';
    pin.title = pinned ? 'Unpin from HUD' : 'Pin to HUD (track missing mats)';
    pin.style.cssText = css('background:transparent', 'border:none', 'cursor:pointer', 'font-size:12px', 'padding:0', 'flex:none', 'width:20px', pinned ? 'opacity:1' : 'opacity:0.35');
    pin.addEventListener('click', (e) => { e.stopPropagation(); this.onPin?.(r.id); this.renderRecipes(); });
    const btn = document.createElement('button');
    btn.textContent = 'Craft';
    btn.disabled = !afford;
    // Affordable = the row's primary action (accent); short/gated = disabled secondary outline.
    btn.style.cssText = btnCss(afford ? 'primary' : 'secondary', true) + ';flex:none' + (afford ? '' : ';opacity:0.4;cursor:default');
    btn.addEventListener('click', () => this.tryCraft(r, gated));
    row.appendChild(sw); row.appendChild(label); row.appendChild(pin); row.appendChild(btn);
    return row;
  }

  // Compact grid tile: a big colour swatch, the name, and a tiny state line. Click the tile to craft;
  // the full cost/time is in the tooltip. A fav star sits in the corner.
  private gridCell(r: Recipe, dev: boolean): HTMLDivElement {
    const { needsResearch, benchGated, needStation, gated, afford } = this.recipeState(r, dev);
    const sel = r === this.selected;
    const cell = document.createElement('div');
    cell.style.cssText = css('position:relative', 'display:flex', 'flex-direction:column', 'align-items:center', 'gap:5px', 'padding:9px 6px 7px',
      `background:${sel ? SK.accentSoft : SK.raised}`, `border:1px solid ${sel ? SK.accent : SK.border}`, `border-radius:${SK.rMd}`, 'cursor:pointer', afford ? 'opacity:1' : 'opacity:0.55');
    const costStr = dev ? 'FREE · instant' : r.cost.map((c) => `${c.amount} ${ITEMS[c.item].name}`).join(', ') + ` · ${craftSeconds(r)}s`;
    const why = needsResearch ? 'Research required' : benchGated ? `Needs ${needStation}` : costStr;
    cell.title = `${ITEMS[r.id].name} — ${why}`;
    cell.addEventListener('mouseenter', () => { cell.style.borderColor = SK.accent; cell.style.background = SK.accentSoft; });
    cell.addEventListener('mouseleave', () => { cell.style.borderColor = sel ? SK.accent : SK.border; cell.style.background = sel ? SK.accentSoft : SK.raised; });
    const sw = document.createElement('div');
    sw.style.cssText = css('width:40px', 'height:40px', `border-radius:${SK.rSm}`, `background:${cssHex(ITEMS[r.id].color)}`, 'flex:none');
    setItemVisual(sw, r.id, cssHex(ITEMS[r.id].color));
    const name = document.createElement('div');
    name.textContent = ITEMS[r.id].name;
    name.style.cssText = css('font-size:10.5px', 'line-height:1.15', 'text-align:center', `color:${SK.text}`, 'max-width:100%', 'overflow:hidden', 'text-overflow:ellipsis', 'white-space:nowrap');
    const sub = document.createElement('div');
    sub.textContent = needsResearch ? 'Research' : benchGated ? `WB ${r.tier}` : dev ? 'FREE' : `${craftSeconds(r)}s`;
    sub.style.cssText = css('font-size:10px', `color:${(needsResearch || benchGated) ? SK.warn : dev ? SK.good : afford ? SK.dim : SK.bad}`);
    cell.appendChild(this.favStar(r, true));
    cell.appendChild(sw); cell.appendChild(name); cell.appendChild(sub);
    // Grid tiles keep their click-to-craft behavior AND now select the tile for the detail pane.
    cell.addEventListener('click', () => { this.selected = r; this.tryCraft(r, gated); this.renderRecipes(); });
    return cell;
  }

  // The favourite ★ toggle used by both views. `corner` positions it absolutely (grid tile top-right).
  private favStar(r: Recipe, corner = false): HTMLButtonElement {
    const star = document.createElement('button');
    const faved = this.favorites.has(r.id);
    star.textContent = faved ? '★' : '☆';
    star.style.cssText = css('background:transparent', 'border:none', 'cursor:pointer', 'font-size:15px', 'padding:0', faved ? `color:${SK.accent}` : `color:${SK.faint}`,
      corner ? 'position:absolute;top:3px;right:5px;z-index:1;line-height:1' : 'flex:none;width:20px');
    star.title = faved ? 'Unfavourite' : 'Favourite';
    star.addEventListener('click', (e) => { e.stopPropagation(); this.toggleFav(r); });
    return star;
  }

  // How many of the selected recipe the current batch selector would enqueue. Max is clamped by what the
  // inventory can actually afford (so a big stockpile cannot queue an unbounded run); dev mode is free, so
  // Max there is a fixed batch. Reuses the same per-ingredient affordability the row buttons use.
  private batchCount(r: Recipe, dev: boolean): number {
    if (this.craftQty !== 'max') return dev ? this.craftQty : Math.min(this.craftQty, this.maxAffordable(r));
    return dev ? DEV_MAX_BATCH : this.maxAffordable(r);
  }

  private maxAffordable(r: Recipe): number {
    let m = MAX_CRAFT_BATCH;
    for (const c of r.cost) m = Math.min(m, Math.floor(this.inv.count(c.item) / Math.max(1, c.amount)));
    return Math.max(0, m);
  }

  // Crafts the selected recipe `batchCount` times through the SAME onCraft path a row's Craft button uses
  // (dev = instant free, else spend + enqueue). The count is pre-clamped to what is affordable, so this
  // never over-enqueues past the inventory.
  private craftSelected(): void {
    const r = this.selected;
    if (!r) return;
    const dev = this.isDev();
    const { gated } = this.recipeState(r, dev);
    const n = this.batchCount(r, dev);
    for (let i = 0; i < n; i++) this.tryCraft(r, gated);
  }

  // COLUMN 3, top: the detail card for the selected recipe -- 64 icon tile + name + description,
  // have/need ingredient list, craft-time line, the x1/x10/Max batch selector, and one block Craft button.
  private renderDetail(): void {
    const box = this.detailBox;
    box.replaceChildren();
    const r = this.selected;
    if (!r) {
      const empty = document.createElement('div');
      empty.textContent = 'Select a recipe to see its details.';
      empty.style.cssText = css(`color:${SK.faint}`, `font-size:${SK.fsSmall}`, 'padding:4px 2px');
      box.appendChild(empty);
      return;
    }
    const dev = this.isDev();
    const { needsResearch, benchGated, needStation, afford } = this.recipeState(r, dev);

    // Header: a 64 icon tile beside the name + a category/station description line.
    const head = document.createElement('div');
    head.style.cssText = css('display:flex', 'align-items:center', 'gap:14px', 'flex:none');
    const tile = slotEl('grid');
    tile.style.width = `${DETAIL_TILE}px`; tile.style.height = `${DETAIL_TILE}px`;
    tile.style.borderColor = SK.borderStrong;
    setItemVisual(tile, r.id, cssHex(ITEMS[r.id].color));
    const titleWrap = document.createElement('div');
    titleWrap.style.cssText = css('flex:1', 'min-width:0');
    const nm = document.createElement('div');
    nm.textContent = ITEMS[r.id].name;
    nm.style.cssText = css(`font:700 ${SK.fsHead} ${SK.font}`, `color:${SK.textHi}`);
    const desc = document.createElement('div');
    // ITEMS carries no per-item description, so the sub-line reports the category + where it can be crafted.
    desc.textContent = `${categoryOf(r.id)} · ${r.tier ? `Workbench Lvl ${r.tier}` : 'Craftable by hand'}`;
    desc.style.cssText = css('margin-top:3px', `font-size:${SK.fsMicro}`, `color:${SK.dim}`);
    titleWrap.append(nm, desc);
    head.append(tile, titleWrap);
    box.appendChild(head);

    // Ingredient have/need list (bad when short); gates read warn, dev reads FREE.
    const mats = document.createElement('div');
    mats.style.cssText = css('display:flex', 'flex-direction:column', 'gap:4px', 'flex:none');
    if (needsResearch || benchGated) {
      const gate = document.createElement('div');
      gate.textContent = needsResearch ? 'Research required' : `Needs ${needStation}`;
      gate.style.cssText = css(`font-size:${SK.fsSmall}`, `color:${SK.warn}`);
      mats.appendChild(gate);
    } else if (dev) {
      const free = document.createElement('div');
      free.textContent = 'FREE · instant';
      free.style.cssText = css(`font-size:${SK.fsSmall}`, `color:${SK.good}`);
      mats.appendChild(free);
    } else {
      for (const c of r.cost) {
        const have = this.inv.count(c.item);
        const ok = have >= c.amount;
        const line = document.createElement('div');
        line.style.cssText = css('display:flex', 'justify-content:space-between', 'gap:10px', `font-size:${SK.fsSmall}`, `color:${SK.text}`);
        const nmEl = document.createElement('span'); nmEl.textContent = ITEMS[c.item].name;
        const qtyEl = document.createElement('span');
        qtyEl.textContent = `${have}/${c.amount}`;
        qtyEl.style.cssText = css('font-variant-numeric:tabular-nums', `color:${ok ? SK.dim : SK.bad}`);
        line.append(nmEl, qtyEl);
        mats.appendChild(line);
      }
    }
    box.appendChild(mats);

    // Craft-time line.
    const time = document.createElement('div');
    time.textContent = `Craft time: ${dev ? 'instant' : `${craftSeconds(r)}s`}`;
    time.style.cssText = css(`font-size:${SK.fsMicro}`, `color:${SK.faint}`, 'flex:none');
    box.appendChild(time);

    // x1 / x10 / Max segmented control.
    const seg = document.createElement('div');
    seg.style.cssText = css('display:flex', 'gap:6px', 'flex:none');
    const opts: Array<{ q: 1 | 10 | 'max'; label: string }> = [{ q: 1, label: '×1' }, { q: 10, label: '×10' }, { q: 'max', label: 'Max' }];
    for (const o of opts) {
      const on = this.craftQty === o.q;
      const b = document.createElement('button');
      b.textContent = o.label;
      b.style.cssText = css('flex:1', 'height:28px', `border-radius:${SK.rMd}`, 'cursor:pointer', `font:700 ${SK.fsMicro} ${SK.font}`, `transition:all ${SK.tFast} ${SK.ease}`,
        on ? `background:${SK.accentSoft};border:1px solid ${SK.accent};color:${SK.textHi}` : `background:transparent;border:1px solid ${SK.borderStrong};color:${SK.dim}`);
      b.addEventListener('click', () => { this.craftQty = o.q; this.renderDetail(); });
      seg.appendChild(b);
    }
    box.appendChild(seg);

    // One block primary Craft button (disabled secondary when short/gated).
    const n = this.batchCount(r, dev);
    const canCraft = afford && (dev || n > 0);
    const qtyTag = this.craftQty === 1 ? '' : this.craftQty === 10 ? ' ×10' : n > 0 ? ` ×${n}` : '';
    const btn = document.createElement('button');
    btn.textContent = `Craft${qtyTag}`;
    btn.disabled = !canCraft;
    btn.style.cssText = btnCss(canCraft ? 'primary' : 'secondary') + ';width:100%;flex:none' + (canCraft ? '' : ';opacity:0.4;cursor:default');
    btn.addEventListener('click', () => this.craftSelected());
    box.appendChild(btn);
  }

  private renderQueue(): void {
    this.queuePane.replaceChildren();
    this.queueBars.length = 0;
    const jobs = this.queue.list();
    if (!jobs.length) {
      const empty = document.createElement('div');
      empty.textContent = 'Nothing crafting.';
      empty.style.cssText = css(`color:${SK.faint}`, `font-size:${SK.fsSmall}`, 'padding:6px 2px');
      this.queuePane.appendChild(empty);
      return;
    }
    jobs.forEach((job, i) => {
      // Each queue entry is a full-width 56px slot (slotEl('queue')) with a 3px accent progress bar pinned
      // to its bottom edge -- only the head job (index 0) actually fills.
      const row = slotEl('queue');
      row.style.width = '100%';
      row.style.height = `${QUEUE_SLOT}px`;
      row.style.borderColor = SK.border;
      row.style.cssText += ';' + css('display:flex', 'align-items:center', 'gap:8px', 'padding:0 10px');

      const sw = document.createElement('span');
      sw.style.cssText = css('width:32px', 'height:32px', `border-radius:${SK.rSm}`, `background:${cssHex(ITEMS[job.recipe.id].color)}`, 'flex:none', 'z-index:1');
      setItemVisual(sw, job.recipe.id, cssHex(ITEMS[job.recipe.id].color));
      const name = document.createElement('span');
      name.textContent = ITEMS[job.recipe.id].name;
      name.style.cssText = css('flex:1', 'min-width:0', 'z-index:1', `font-size:${SK.fsSmall}`, `color:${SK.text}`, 'overflow:hidden', 'text-overflow:ellipsis', 'white-space:nowrap');
      const secs = document.createElement('span');
      secs.textContent = i === 0 ? '…' : `${Math.ceil(job.totalMs / 1000)}s`;
      secs.style.cssText = css('z-index:1', `font-size:${SK.fsSmall}`, `color:${SK.dim}`, 'font-variant-numeric:tabular-nums');
      const x = document.createElement('button');
      x.textContent = '✕';
      x.title = 'Cancel (refunds the cost)';
      x.style.cssText = css('z-index:1', 'background:transparent', 'border:none', `color:${SK.bad}`, 'cursor:pointer', 'font:inherit', 'font-size:13px', 'padding:0 2px');
      x.addEventListener('click', () => this.onCancel?.(i));

      // Reorder arrows (owner QoL): bump a job up/down the queue. Hidden where the move is impossible.
      const arrowCss = css('z-index:1', 'background:transparent', 'border:none', `color:${SK.dim}`, 'cursor:pointer', 'font:inherit', 'font-size:11px', 'padding:0 1px');
      const up = document.createElement('button');
      up.textContent = '▲'; up.title = 'Craft sooner'; up.style.cssText = arrowCss;
      up.style.visibility = i === 0 ? 'hidden' : 'visible';
      up.addEventListener('click', () => { this.queue.move(i, -1, performance.now()); });
      const dn = document.createElement('button');
      dn.textContent = '▼'; dn.title = 'Craft later'; dn.style.cssText = arrowCss;
      dn.style.visibility = i === jobs.length - 1 ? 'hidden' : 'visible';
      dn.addEventListener('click', () => { this.queue.move(i, 1, performance.now()); });

      const track = document.createElement('div'); // 3px accent progress bar on the active (head) job
      track.style.cssText = css('position:absolute', 'left:0', 'right:0', 'bottom:0', `height:${QUEUE_BAR_H}px`, 'background:rgba(0,0,0,0.4)', 'overflow:hidden', 'pointer-events:none', 'z-index:2');
      const fill = document.createElement('div');
      fill.style.cssText = css('height:100%', 'width:0%', `background:${SK.accent}`);
      track.appendChild(fill);

      row.appendChild(sw); row.appendChild(name); row.appendChild(secs); row.appendChild(up); row.appendChild(dn); row.appendChild(x); row.appendChild(track);
      this.queuePane.appendChild(row);
      this.queueBars.push({ fill, secs, index: i });
    });
  }
}

// Always-on-screen mini queue (bottom-left): the head job's name + a thin progress bar, plus a "+N"
// badge for the jobs behind it. Hidden when the queue is empty. Ticked from the main frame loop.
export class QueueHud {
  private readonly root: HTMLDivElement;
  private readonly nameEl: HTMLDivElement;
  private readonly fill: HTMLDivElement;
  private readonly more: HTMLDivElement;

  constructor(private readonly queue: CraftQueue) {
    this.root = document.createElement('div');
    // Sits above the bottom-left vitals stack: same left edge (20px) and width (236px) as the bars, with a
    // 13px gap above them (vitals: bottom 20px + ~77px tall = top ~97px, +13 = 110px).
    this.root.style.cssText = css(
      'position:fixed', 'left:20px', 'bottom:110px', 'z-index:9', 'display:none', 'width:236px', 'box-sizing:border-box',
      'padding:8px 10px', `background:${SK.glassLt}`, `border:1px solid ${SK.border}`,
      `backdrop-filter:blur(${SK.blur})`, `-webkit-backdrop-filter:blur(${SK.blur})`,
      `border-radius:${SK.rMd}`, `font:${SK.fsSmall}/1.3 ${SK.font}`, `color:${SK.text}`, 'pointer-events:none',
    );
    const row = document.createElement('div');
    row.style.cssText = css('display:flex', 'justify-content:space-between', 'gap:8px', 'margin-bottom:5px');
    this.nameEl = document.createElement('div');
    this.more = document.createElement('div');
    this.more.style.cssText = css(`color:${SK.dim}`, 'font-variant-numeric:tabular-nums');
    row.appendChild(this.nameEl); row.appendChild(this.more);
    const track = document.createElement('div');
    track.style.cssText = css('height:3px', 'border-radius:2px', 'background:rgba(0,0,0,0.55)', 'overflow:hidden');
    this.fill = document.createElement('div');
    this.fill.style.cssText = css('height:100%', 'width:0%', `background:${SK.accent}`);
    track.appendChild(this.fill);
    this.root.appendChild(row);
    this.root.appendChild(track);
    document.body.appendChild(this.root);
  }

  update(now: number): void {
    const jobs = this.queue.list();
    if (!jobs.length) { if (this.root.style.display !== 'none') this.root.style.display = 'none'; return; }
    if (this.root.style.display === 'none') this.root.style.display = 'block';
    const head = jobs[0];
    this.nameEl.textContent = ITEMS[head.recipe.id].name;
    this.more.textContent = jobs.length > 1 ? `+${jobs.length - 1} · ${Math.ceil(this.queue.remainingSec(0, now))}s` : `${Math.ceil(this.queue.remainingSec(0, now))}s`;
    this.fill.style.width = `${(this.queue.progress(0, now) * 100).toFixed(1)}%`;
  }
}

function css(...lines: string[]): string { return lines.join(';'); }
function cssHex(hex: number): string { return '#' + hex.toString(16).padStart(6, '0'); }
function loadFavorites(): string[] {
  try { const raw = localStorage.getItem(FAV_KEY); return raw ? (JSON.parse(raw) as string[]) : []; } catch { return []; }
}
function saveFavorites(set: Set<string>): void {
  try { localStorage.setItem(FAV_KEY, JSON.stringify([...set])); } catch { /* ignore */ }
}
const VIEW_KEY = 'oxide.craftView';
function loadView(): 'list' | 'grid' {
  try { return localStorage.getItem(VIEW_KEY) === 'grid' ? 'grid' : 'list'; } catch { return 'list'; }
}
function saveView(v: 'list' | 'grid'): void {
  try { localStorage.setItem(VIEW_KEY, v); } catch { /* ignore */ }
}
